import React from "react";
import { ClientFactory } from "@/main/common/ClientFactory";
import type { HandoffCandidate } from "@/main/common/HandoffTypes";
import logger from "@/lib/logger";

/**
 * The one hand-off fetch, made AFTER the published post's page has landed (empty-room design §4): never
 * in the publish path, once per arming, never on a refresh.
 *
 * <p>`armed` is the page's reading of the one-shot `sv_m` publish parameter, which the page strips from
 * the URL on first paint — so a reload arrives un-armed and this hook stays idle (D14: no re-fetch, and
 * the server-side offer row is written only by the call this hook makes). The flag is latched: once a
 * fetch has been issued for a mount it is not repeated if `armed` flickers.
 *
 * <p>Renders nothing (returns `null`) on 204, on any failure, on a malformed body, and when the offered
 * post is the viewer's own (the server excludes it; the check here is the rule-32 posture of never
 * trusting a payload with the author's own post, since a wrong offer is worse than none).
 */
export function useHandoffCandidate(armed: boolean, ownDiscussionPublicId: string | null | undefined): HandoffCandidate | null {
    const [candidate, setCandidate] = React.useState<HandoffCandidate | null>(null);
    const requestedRef = React.useRef(false);

    React.useEffect(() => {
        if (!armed || requestedRef.current) {
            return;
        }
        requestedRef.current = true;
        let cancelled = false;
        // Through a resolved promise, so a synchronous throw from the client (a partial test double, an
        // interceptor) lands in the same catch as a rejected request: the offer must never take the
        // author's post page down with it (rule 32).
        void Promise.resolve()
            .then(() => ClientFactory.getNextHandoffCandidate())
            .then((response) => {
                if (cancelled) {
                    return;
                }
                const body = response.status === 200 ? response.data : undefined;
                const next = isRenderableCandidate(body, ownDiscussionPublicId) ? body : null;
                setCandidate(next);
            })
            .catch((error) => {
                // HANDOFF_FETCH_FAILED: the offer is additive, so a failure renders nothing and the
                // author still has their complete post page (rule 32). Warn, never error: an error here
                // would page the island-health guard for a feature whose absence is its correct
                // degraded state.
                logger.warn("[HandoffCard] HANDOFF_FETCH_FAILED: the hand-off candidate could not be fetched", error);
            });
        return () => {
            cancelled = true;
        };
    }, [armed, ownDiscussionPublicId]);

    return candidate;
}

function isRenderableCandidate(body: unknown, ownDiscussionPublicId: string | null | undefined): body is HandoffCandidate {
    if (!body || typeof body !== "object") {
        return false;
    }
    const value = body as Partial<HandoffCandidate>;
    if (!value.discussionPublicId || !value.title || !value.authorUsername || !value.url) {
        return false;
    }
    if (ownDiscussionPublicId && value.discussionPublicId === ownDiscussionPublicId) {
        return false;
    }
    return typeof value.url === "string" && value.url.startsWith("/");
}
